import { StandardEvents } from '@shopify/events';

const CART_DRAWER_ID = 'cart-drawer';
const WISHLIST_DRAWER_ID = 'khushi-wishlist-drawer';

class KhushiDrawerPricing {
  constructor() {
    this.cartDrawer = null;
    this.cartObserver = null;
    this.cartFrame = 0;
    this.wishlistFrame = 0;

    this.mountCart();
    document.addEventListener('theme-drawer:open', this.handleDrawerOpen);
    document.addEventListener('shopify:section:load', this.handleSectionLoad);
    document.addEventListener(StandardEvents.cartLinesUpdate, this.handleCartPricingUpdate);
    document.addEventListener(StandardEvents.cartDiscountUpdate, this.handleCartPricingUpdate);
    window.addEventListener('khushi:wishlist:changed', this.handleWishlistChange);
    window.addEventListener('khushi:wishlist:refreshed', this.handleWishlistChange);
  }

  handleDrawerOpen = (event) => {
    if (event.target?.id === CART_DRAWER_ID) {
      this.mountCart();
      this.scheduleCartSync();
      return;
    }

    if (event.target?.id === WISHLIST_DRAWER_ID) {
      this.syncWishlistAfterRefresh();
    }
  };

  handleSectionLoad = () => {
    this.mountCart();
    this.scheduleCartSync();
  };

  handleCartPricingUpdate = (event) => {
    this.mountCart();
    this.shimmerCartTotal();

    const promise = event.promise;
    if (!promise) {
      this.scheduleCartSync();
      return;
    }

    promise
      .then(() => this.scheduleCartSync())
      .catch(() => this.scheduleCartSync())
      .finally(() => {
        requestAnimationFrame(() => this.clearCartTotalShimmer());
      });
  };

  handleWishlistChange = () => {
    const drawer = document.getElementById(WISHLIST_DRAWER_ID);
    if (drawer?.hasAttribute('open')) this.scheduleWishlistSync();
  };

  mountCart() {
    const drawer = document.getElementById(CART_DRAWER_ID);
    if (!drawer || drawer === this.cartDrawer) return;

    this.cartObserver?.disconnect();
    this.cartDrawer = drawer;
    this.cartObserver = new MutationObserver(() => this.scheduleCartSync());
    this.cartObserver.observe(drawer, { childList: true, subtree: true });
    this.scheduleCartSync();
  }

  scheduleCartSync() {
    if (this.cartFrame) return;
    this.cartFrame = requestAnimationFrame(() => {
      this.cartFrame = 0;
      this.syncCart();
    });
  }

  scheduleWishlistSync() {
    if (this.wishlistFrame) return;
    this.wishlistFrame = requestAnimationFrame(() => {
      this.wishlistFrame = 0;
      void this.syncWishlist();
    });
  }

  syncWishlistAfterRefresh() {
    const wishlist = window.khushiWishlist;
    if (!wishlist) return;

    Promise.resolve(wishlist.refreshItemsMetadata?.())
      .catch(() => undefined)
      .finally(() => this.scheduleWishlistSync());
  }

  shimmerCartTotal() {
    const sellingPrice = this.cartDrawer?.querySelector(
      '.khushi-drawer-price--total [data-khushi-drawer-selling-price]'
    );
    if (!(sellingPrice instanceof HTMLElement)) return;

    if (typeof sellingPrice.shimmer === 'function') sellingPrice.shimmer();
    else sellingPrice.setAttribute('shimmer', '');
  }

  clearCartTotalShimmer() {
    this.cartDrawer
      ?.querySelectorAll('.khushi-drawer-price--total [data-khushi-drawer-selling-price][shimmer]')
      .forEach((element) => element.removeAttribute('shimmer'));
  }

  cloneSource(source) {
    if (!(source instanceof Element)) return null;
    return source.firstElementChild?.cloneNode(true) || null;
  }

  syncPriceNode(container, source, nativePrice) {
    if (!(container instanceof Element) || !(nativePrice instanceof Element)) return;

    nativePrice.setAttribute('data-khushi-drawer-native-price', '');

    const incoming = this.cloneSource(source);
    if (!(incoming instanceof Element)) return;

    const existing = container.querySelector(':scope > [data-khushi-drawer-enhanced-price]');
    const incomingSignature = incoming.getAttribute('data-khushi-price-signature') || '';
    const existingSignature = existing?.getAttribute('data-khushi-price-signature') || '';

    if (existing && incomingSignature && incomingSignature === existingSignature) return;

    if (existing) {
      existing.replaceWith(incoming);
      return;
    }

    container.insertBefore(incoming, nativePrice);
  }

  syncCart() {
    const drawer = this.cartDrawer;
    const sources = drawer?.querySelector('[data-khushi-cart-pricing-sources]');
    if (!drawer || !sources) return;

    const itemSources = Array.from(sources.querySelectorAll('[data-khushi-cart-item-price]'));

    drawer.querySelectorAll('.cart-items__table-row[data-key]').forEach((row) => {
      const target = row.querySelector('.cart-items__unit-price-wrapper');
      if (!target) return;

      const key = row.getAttribute('data-key') || '';
      const source = itemSources.find((entry) => entry.dataset.khushiCartItemPrice === key);
      const nativePrice = target.querySelector(':scope > div:not([data-khushi-drawer-enhanced-price])');
      if (!source || !nativePrice) return;

      this.syncPriceNode(target, source, nativePrice);
    });

    const totalSource = sources.querySelector('[data-khushi-cart-total-price]');
    const totalRow = drawer.querySelector('.cart-totals__total');
    const nativeTotal = totalRow?.querySelector('[data-testid="cart-total-value"]');
    if (totalSource && totalRow && nativeTotal) {
      this.syncPriceNode(totalRow, totalSource, nativeTotal);
    }
  }

  createTextGroup(className, text, hiddenLabel = '') {
    const group = document.createElement('span');
    group.className = `price-item__group ${className}`;
    if (hiddenLabel) {
      const label = document.createElement('span');
      label.className = 'visually-hidden';
      label.textContent = hiddenLabel;
      group.appendChild(label);
    }
    group.appendChild(document.createTextNode(text));
    return group;
  }

  createWishlistPrice(wishlist, price, compareAtPrice) {
    const current = Number(price) || 0;
    const compare = Number(compareAtPrice) || 0;
    const hasCompare = compare > current;
    const enhanced = document.createElement('div');
    enhanced.className = 'khushi-price khushi-drawer-price--wishlist';
    enhanced.setAttribute('data-khushi-drawer-enhanced-price', '');

    if (hasCompare) {
      const discountPercentage = Math.max(1, Math.min(100, Math.round(((compare - current) * 100) / compare)));
      const discount = document.createElement('span');
      discount.className = 'price-item__group khushi-price__discount';
      discount.dataset.khushiDiscountPercentage = String(discountPercentage);

      const visibleDiscount = document.createElement('span');
      visibleDiscount.setAttribute('aria-hidden', 'true');
      const arrow = document.createElement('span');
      arrow.className = 'khushi-price__discount-arrow';
      arrow.textContent = '↓';
      visibleDiscount.append(arrow, document.createTextNode(`${discountPercentage}%`));

      const hiddenDiscount = document.createElement('span');
      hiddenDiscount.className = 'visually-hidden';
      hiddenDiscount.textContent = `${discountPercentage}% off`;
      discount.append(visibleDiscount, hiddenDiscount);

      enhanced.append(
        discount,
        this.createTextGroup('price-item--regular compare-at-price khushi-price__mrp', wishlist.formatMoney(compare), 'Regular price '),
        this.createTextGroup('price khushi-price__selling', wishlist.formatMoney(current), 'Sale price ')
      );
    } else {
      enhanced.append(this.createTextGroup('price khushi-price__selling', wishlist.formatMoney(current)));
    }

    return enhanced;
  }

  async syncWishlist() {
    const wishlist = window.khushiWishlist;
    const drawer = document.getElementById(WISHLIST_DRAWER_ID);
    if (!wishlist || !drawer) return;

    const rows = Array.from(drawer.querySelectorAll('[data-wishlist-product]'));
    await Promise.all(rows.map(async (row) => {
      const priceContainer = row.querySelector('.khushi-wishlist-item__price');
      if (!priceContainer || priceContainer.querySelector('[data-khushi-drawer-enhanced-price]')) return;

      const productId = row.getAttribute('data-wishlist-product') || '';
      const item = wishlist.items?.find((entry) => String(entry.productId) === productId);
      if (!item) return;

      let compareAtPrice = 0;
      let currentPrice = Number(item.price) || 0;
      const url = item.url || (item.handle ? `/products/${item.handle}` : '');

      if (url && typeof wishlist.fetchProduct === 'function') {
        try {
          const product = await wishlist.fetchProduct(url);
          const variant = product.variants?.find((entry) => String(entry.id) === String(item.variantId || ''));
          if (variant) {
            currentPrice = Number(variant.price ?? currentPrice) || currentPrice;
            compareAtPrice = Number(variant.compare_at_price ?? 0) || 0;
          }
        } catch {
          compareAtPrice = 0;
        }
      }

      if (!priceContainer.isConnected || priceContainer.querySelector('[data-khushi-drawer-enhanced-price]')) return;

      const nativeText = priceContainer.textContent?.trim() || wishlist.formatMoney(currentPrice);
      priceContainer.textContent = '';

      const enhanced = this.createWishlistPrice(wishlist, currentPrice, compareAtPrice);
      const nativePrice = document.createElement('span');
      nativePrice.setAttribute('data-khushi-drawer-native-price', '');
      nativePrice.textContent = nativeText;
      priceContainer.append(enhanced, nativePrice);
    }));
  }
}

const startKhushiDrawerPricing = () => {
  if (!window.khushiDrawerPricing) window.khushiDrawerPricing = new KhushiDrawerPricing();
};

if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', startKhushiDrawerPricing, { once: true });
} else {
  startKhushiDrawerPricing();
}
